import { createSlice } from '@reduxjs/toolkit'
import { orderBy, unionBy, uniq } from 'es-toolkit'
import { floor } from 'es-toolkit/compat'

import * as API from 'api/reports/reports'
import type {
  CreateExportDataInfo,
  DailyPlanAccuracy,
  DailyWork,
  HourlyPlanAccuracy,
  ProductivityGroup,
  ProductivityWorker,
  ReportAverage,
  ReportAverageResponse,
  ReportDailyPlanAccuracyResponse,
  ReportHourlyPlanAccuracyResponse,
  ReportProductivityResponse,
  ReportPlanHistoryResponse,
} from 'api/reports/types'

import { downloadByURL, getSplitPeriods, handleApiError } from 'slices/utils'

import { showError } from './notificationSlice'

import type { PayloadAction } from '@reduxjs/toolkit'
import type { AppThunk, RootState } from 'store'

type ReportAverageData = {
  data: ReportAverage[]
  workspaceId: number | undefined
  from: string | undefined
  to: string | undefined
}

type ReportProductivityData = {
  data: ReportProductivityResponse | undefined
  workspaceId: number | undefined
  from: string | undefined
  to: string | undefined
}

type HourlyPlanAccuraciesData = {
  data: HourlyPlanAccuracy[]
  workspaceId: number | undefined
  date: string | undefined
}

type DailyPlanAccuraciesData = {
  data: DailyPlanAccuracy[]
  workspaceId: number | undefined
  from: string | undefined
  to: string | undefined
}

type ReportsState = {
  isRequesting: boolean
  errorMessage: string
  reportAverages?: ReportAverageData
  reportAveragesDailyReport?: ReportAverageData
  productivity?: ReportProductivityData
  productivityDailyReport?: ReportProductivityData
  hourlyPlanAccuracies?: HourlyPlanAccuraciesData
  hourlyPlanAccuraciesDailyReport?: HourlyPlanAccuraciesData
  dailyPlanAccuracies?: DailyPlanAccuraciesData
  dailyPlanAccuraciesDailyReport?: DailyPlanAccuraciesData
  planHistory?: ReportPlanHistoryResponse
  downloadUrl?: string
}

const initialState: ReportsState = {
  isRequesting: false,
  errorMessage: '',
}

interface ExtendedDailyWork extends DailyWork {
  hourlyAvgProductivityPool: (number | null)[]
}
interface ExtendedProductivityWorker extends ProductivityWorker {
  dailyWorkData: ExtendedDailyWork[]
}
interface ExtendedProductivityGroup extends ProductivityGroup {
  workers: ExtendedProductivityWorker[]
}
interface ExtendedReportProductivity extends ReportProductivityResponse {
  groups: ExtendedProductivityGroup[]
  dailyWorkData: ExtendedDailyWork[]
}

// null を除いた平均を求める
export const compactAverage = (baseData: Array<number | null>) => {
  const data = baseData.filter((n): n is number => n !== null)
  if (data.length === 0) {
    return null
  }
  const average = data.reduce((cur, acc) => cur + acc, 0) / data.length
  return floor(average, 1)
}

// hourlyAvgProductivity の集計をするために productivityList を拡張する
const extendProductivityList = (productivityList: ReportProductivityResponse[]) => {
  return productivityList.map<ExtendedReportProductivity>(productivity => {
    const groups = productivity.groups.map<ExtendedProductivityGroup>(productivityGroup => {
      const workers = productivityGroup.workers.map<ExtendedProductivityWorker>(productivityWorker => {
        const dailyWorkData = productivityWorker.dailyWorkData.map<ExtendedDailyWork>(dailyWork => {
          return { ...dailyWork, hourlyAvgProductivityPool: [dailyWork.hourlyAvgProductivity] }
        })
        return { ...productivityWorker, dailyWorkData }
      })
      return { ...productivityGroup, workers }
    })
    const dailyWorkData = productivity.dailyWorkData.map<ExtendedDailyWork>(dailyWork => {
      return { ...dailyWork, hourlyAvgProductivityPool: [dailyWork.hourlyAvgProductivity] }
    })
    return { ...productivity, groups, dailyWorkData }
  })
}

// hourlyAvgProductivityPool を集計する
const accumulateProductivity = (extendedProductivity: ExtendedReportProductivity) => {
  const accumulatedGroups = extendedProductivity.groups.map<ProductivityGroup>(productivityGroup => {
    const workers = productivityGroup.workers.map<ProductivityWorker>(productivityWorker => {
      const dailyWorkData = productivityWorker.dailyWorkData.map<DailyWork>(dailyWork => {
        return {
          scheduleTypeId: dailyWork.scheduleTypeId,
          scheduleTypeName: dailyWork.scheduleTypeName,
          scheduleTypeColor: dailyWork.scheduleTypeColor,
          unit: dailyWork.unit,
          hourlyAvgProductivity: compactAverage(dailyWork.hourlyAvgProductivityPool),
          data: dailyWork.data,
        }
      })
      return { ...productivityWorker, dailyWorkData }
    })
    return { ...productivityGroup, workers }
  })
  const dailyWorkData = extendedProductivity.dailyWorkData.map<ExtendedDailyWork>(dailyWork => {
    return {
      ...dailyWork,
      hourlyAvgProductivity: compactAverage(dailyWork.hourlyAvgProductivityPool),
    }
  })
  return { ...extendedProductivity, groups: accumulatedGroups, dailyWorkData }
}

export const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    startRequest: state => {
      state.isRequesting = true
      state.errorMessage = ''
    },
    clearErrorMessage: state => {
      state.errorMessage = ''
    },
    apiFailure: (state, action: PayloadAction<{ errorMessage: string }>) => {
      state.isRequesting = false
      state.errorMessage = action.payload.errorMessage
    },
    getReportAverageSuccess: (
      state,
      action: PayloadAction<{
        response: ReportAverageResponse
        workspaceId: number
        from: string
        to: string
        isDailyReport: boolean
      }>
    ) => {
      state.isRequesting = false
      if (action.payload.isDailyReport) {
        state.reportAveragesDailyReport = {
          data: action.payload.response.data,
          workspaceId: action.payload.workspaceId,
          from: action.payload.from,
          to: action.payload.to,
        }
        return
      }
      state.reportAverages = {
        data: action.payload.response.data,
        workspaceId: action.payload.workspaceId,
        from: action.payload.from,
        to: action.payload.to,
      }
    },
    getReportProductivitySuccess: (
      state,
      action: PayloadAction<{
        response: ReportProductivityResponse
        workspaceId: number
        from: string
        to: string
        isDailyReport: boolean
      }>
    ) => {
      state.isRequesting = false
      if (action.payload.isDailyReport) {
        state.productivityDailyReport = {
          data: action.payload.response,
          workspaceId: action.payload.workspaceId,
          from: action.payload.from,
          to: action.payload.to,
        }
        if (state.productivityDailyReport.data) {
          state.productivityDailyReport.data.groups = orderBy(action.payload.response.groups, ['groupName'], ['asc'])
        }
        return
      }
      state.productivity = {
        data: action.payload.response,
        workspaceId: action.payload.workspaceId,
        from: action.payload.from,
        to: action.payload.to,
      }
      if (state.productivity.data) {
        state.productivity.data.groups = orderBy(action.payload.response.groups, ['groupName'], ['asc'])
      }
    },
    getReportPlanAccuracySuccess: (
      state,
      action: PayloadAction<{
        response: ReportDailyPlanAccuracyResponse
        workspaceId: number
        from: string
        to: string
        isDailyReport: boolean
      }>
    ) => {
      state.isRequesting = false
      if (action.payload.isDailyReport) {
        state.dailyPlanAccuraciesDailyReport = {
          data: action.payload.response.data,
          workspaceId: action.payload.workspaceId,
          from: action.payload.from,
          to: action.payload.to,
        }
        return
      }
      state.dailyPlanAccuracies = {
        data: action.payload.response.data,
        workspaceId: action.payload.workspaceId,
        from: action.payload.from,
        to: action.payload.to,
      }
    },
    getReportHourlyPlanAccuracySuccess: (
      state,
      action: PayloadAction<{
        response: ReportHourlyPlanAccuracyResponse
        workspaceId: number
        date: string
        isDailyReport: boolean
      }>
    ) => {
      state.isRequesting = false
      if (action.payload.isDailyReport) {
        state.hourlyPlanAccuraciesDailyReport = {
          data: action.payload.response.data,
          workspaceId: action.payload.workspaceId,
          date: action.payload.date,
        }
        return
      }
      state.hourlyPlanAccuracies = {
        data: action.payload.response.data,
        workspaceId: action.payload.workspaceId,
        date: action.payload.date,
      }
    },
    getReportPlanHistorySuccess: (state, action: PayloadAction<ReportPlanHistoryResponse>) => {
      state.isRequesting = false
      state.planHistory = action.payload
    },
    getExportDataUrlSuccess: state => {
      state.isRequesting = false
    },
  },
})

export const {
  startRequest,
  clearErrorMessage,
  apiFailure,
  getReportAverageSuccess,
  getReportProductivitySuccess,
  getReportPlanAccuracySuccess,
  getReportHourlyPlanAccuracySuccess,
  getReportPlanHistorySuccess,
  getExportDataUrlSuccess,
} = reportsSlice.actions

export const getReportAverage =
  (workspaceId: number, from: string, to: string, isDailyReport: boolean): AppThunk =>
  async dispatch => {
    dispatch(startRequest())

    try {
      const res = await API.getReportAverage(workspaceId, from, to, isDailyReport)
      dispatch(getReportAverageSuccess({ response: res, workspaceId, from, to, isDailyReport }))
    } catch (res) {
      handleApiError(res, dispatch, apiFailure)
    }
  }

const dailyWorkDataMerge = (dstData: ExtendedDailyWork[], srcData: ExtendedDailyWork[]) => {
  const scheduleTypeIds = uniq([...dstData.map(d => d.scheduleTypeId), ...srcData.map(d => d.scheduleTypeId)])
  return scheduleTypeIds.map(id => {
    const dstTarget = dstData.find(d => d.scheduleTypeId === id)
    const srcTarget = srcData.find(d => d.scheduleTypeId === id)
    if (dstTarget && srcTarget) {
      return {
        ...srcTarget,
        data: unionBy(dstTarget.data, srcTarget.data, target => target.date),
        hourlyAvgProductivityPool: [...dstTarget.hourlyAvgProductivityPool, ...srcTarget.hourlyAvgProductivityPool],
      }
    }
    return (dstTarget || srcTarget)!
  })
}

const workerMerge = (dstWorkers: ExtendedProductivityWorker[], srcWorkers: ExtendedProductivityWorker[]) => {
  const workerIds = uniq([...dstWorkers.map(w => w.workerId), ...srcWorkers.map(w => w.workerId)])
  return workerIds.map(id => {
    const dstTarget = dstWorkers.find(w => w.workerId === id)
    const srcTarget = srcWorkers.find(w => w.workerId === id)
    if (dstTarget && srcTarget) {
      return {
        ...dstTarget,
        dailyWorkData: dailyWorkDataMerge(dstTarget.dailyWorkData, srcTarget.dailyWorkData),
      }
    }
    return (dstTarget || srcTarget)!
  })
}

const groupMerge = (dstGroups: ExtendedProductivityGroup[], srcGroups: ExtendedProductivityGroup[]) => {
  // groupIdがnullの時、未所属のグループか、応援を示すグループの2通りがあるため、未所属のグループを取得するときはsupportedWorkspaceIdがnullのものを選択する
  const dstWithoutSupportGroup = dstGroups.filter(g => g.supportedWorkspaceId === null)
  const srcWithoutSupportGroup = srcGroups.filter(g => g.supportedWorkspaceId === null)
  const groupIds = uniq([...dstWithoutSupportGroup.map(g => g.groupId), ...srcWithoutSupportGroup.map(g => g.groupId)])

  const supportedWorkspaceIds = uniq([
    ...dstGroups.map(g => g.supportedWorkspaceId),
    ...srcGroups.map(g => g.supportedWorkspaceId),
  ]).filter(id => id !== null)

  return groupIds
    .map(id => {
      const dstTarget = dstWithoutSupportGroup.find(g => g.groupId === id)
      const srcTarget = srcWithoutSupportGroup.find(g => g.groupId === id)
      if (dstTarget && srcTarget) {
        return {
          ...dstTarget,
          workers: workerMerge(dstTarget.workers, srcTarget.workers),
        }
      }
      return (dstTarget || srcTarget)!
    })
    .concat(
      supportedWorkspaceIds.map(id => {
        const dstTarget = dstGroups.find(g => g.supportedWorkspaceId === id)
        const srcTarget = srcGroups.find(g => g.supportedWorkspaceId === id)
        if (dstTarget && srcTarget) {
          return {
            ...dstTarget,
            workers: workerMerge(dstTarget.workers, srcTarget.workers),
          }
        }
        return (dstTarget || srcTarget)!
      })
    )
}

export const getReportProductivity =
  (workspaceId: number, from: string, to: string, isDailyReport: boolean): AppThunk =>
  async dispatch => {
    dispatch(startRequest())

    try {
      const splitPeriods = getSplitPeriods(from, to)
      const promises: Promise<ReportProductivityResponse>[] = splitPeriods.map((period: [string, string]) =>
        API.getReportProductivity(workspaceId, period[0], period[1], isDailyReport)
      )

      const productivityList = await Promise.all(promises)

      // hourlyAvgProductivity の集計をするために productivityList を拡張する
      const extendedProductivityList = extendProductivityList(productivityList)

      // 複数の productivity を合成する
      const productivity = extendedProductivityList.reduce(
        (acc: ExtendedReportProductivity, cur) => {
          // dailyWorkData を合成する
          // ワークスペースが存在しない期間の場合、acc.dailyWorkDataが空配列になってしまうため、cur.dailyWorkDataからデータを取り込む
          const dailyWorkData = cur.dailyWorkData.map(dailyWork => {
            const existingData = acc.dailyWorkData.find(d => d.scheduleTypeId === dailyWork.scheduleTypeId)
            return existingData
              ? {
                  ...dailyWork,
                  data: dailyWork.data.concat(existingData.data || []),
                  hourlyAvgProductivityPool: dailyWork.hourlyAvgProductivityPool.concat(
                    existingData.hourlyAvgProductivityPool
                  ),
                }
              : dailyWork
          })

          return {
            ...acc,
            dailyWorkData,
            groups: groupMerge(acc.groups, cur.groups),
          }
        },
        {
          workspaceId: workspaceId,
          workspaceName: extendedProductivityList[0].workspaceName,
          dailyWorkData: [],
          groups: [],
        }
      )

      // hourlyAvgProductivity を集計する
      const accumulatedProductivity = accumulateProductivity(productivity)

      dispatch(
        getReportProductivitySuccess({ response: accumulatedProductivity, workspaceId, from, to, isDailyReport })
      )
    } catch (res) {
      handleApiError(res, dispatch, apiFailure)
    }
  }

export const getReportPlanAccuracy =
  (workspaceId: number, from: string, to: string, isDailyReport: boolean): AppThunk =>
  async dispatch => {
    dispatch(startRequest())

    try {
      const res = await API.getReportPlanAccuracy(workspaceId, from, to, isDailyReport)
      dispatch(getReportPlanAccuracySuccess({ response: res, workspaceId, from, to, isDailyReport }))
    } catch (res) {
      handleApiError(res, dispatch, apiFailure)
    }
  }

export const getReportHourlyPlanAccuracy =
  (workspaceId: number, date: string, isDailyReport: boolean): AppThunk =>
  async dispatch => {
    dispatch(startRequest())

    try {
      const res = await API.getReportHourlyPlanAccuracy(workspaceId, date, isDailyReport)
      dispatch(getReportHourlyPlanAccuracySuccess({ response: res, workspaceId, date, isDailyReport }))
    } catch (res) {
      handleApiError(res, dispatch, apiFailure)
    }
  }

export const getReportPlanHistory =
  (workspaceId: number, date: string, page: number, limit: number): AppThunk =>
  async dispatch => {
    dispatch(startRequest())

    try {
      const res = await API.getReportPlanHistory(workspaceId, date, page, limit)
      dispatch(getReportPlanHistorySuccess(res))
    } catch (res) {
      handleApiError(res, dispatch, apiFailure)
    }
  }

export const exportReport =
  (data: CreateExportDataInfo, fileName: string): AppThunk =>
  async dispatch => {
    dispatch(startRequest())

    try {
      const { requestId } = await API.createExportData(data)
      if (!requestId) {
        return
      }
      const fetchExportData = async () => {
        const exportDataResponse = await API.getExportData(requestId)
        if (exportDataResponse?.downloadUrl) {
          dispatch(getExportDataUrlSuccess())
          return await downloadByURL(exportDataResponse.downloadUrl, fileName)
        }
        await fetchExportData()
      }
      try {
        await fetchExportData()
      } catch {
        dispatch(showError())
      }
    } catch (err) {
      handleApiError(err, dispatch, apiFailure)
    }
  }

export const selectReportsStatus = (state: RootState) => ({ ...state.reports })

export default reportsSlice.reducer
